// src/ConsumerApp/hooks/useNavigationExitControl.js

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const SESSION_ENTRY_KEY = 'bb_spa_entry_idx';

export function useNavigationExitControl() {
  const location = useLocation();
  const isNavigatingRef = useRef(false);

  // 1. Establish the original external entry index
  useEffect(() => {
    const currentIdx = window.history.state?.idx ?? 0;
    const storedEntry = sessionStorage.getItem(SESSION_ENTRY_KEY);

    if (storedEntry === null) {
      sessionStorage.setItem(SESSION_ENTRY_KEY, String(currentIdx));
    }
  }, []);

  // 2. Intercept native browser Back button when landing on or leaving Home
  useEffect(() => {
    const isHome =
      location.pathname === '/consumer' ||
      location.pathname === '/consumer/' ||
      location.pathname === '/consumer/HomeScreen';

    if (!isHome) return;

    const handlePopState = (event) => {
      if (isNavigatingRef.current) return;

      const entryIdx = Number(sessionStorage.getItem(SESSION_ENTRY_KEY) || 0);
      const currentIdx = event.state?.idx ?? 0;

      // If user hit browser Back while on Home, and the previous stack entry
      // is still an internal page (idx >= entryIdx), jump straight out of the app.
      if (currentIdx >= entryIdx) {
        isNavigatingRef.current = true;

        // Calculate steps needed to jump before the initial session entry
        const deltaToExit = -(currentIdx - entryIdx + 1);

        if (deltaToExit < 0) {
          window.history.go(deltaToExit);
        } else {
          // If already at initial boundary and external referrer exists, fallback safely
          if (document.referrer && !document.referrer.includes(window.location.host)) {
            window.location.href = document.referrer;
          }
        }

        setTimeout(() => {
          isNavigatingRef.current = false;
        }, 300);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [location.pathname]);
}